import { TAG_MEASUREMENT_SYSTEM, TAG_NOTIFICATIONS, TAG_PROJECTS } from "src/api/api-tags";
import { computeQuotePropertyChanges } from "src/api/project-slice-set-project-quote-user-property";
import { quoteApiSlice } from 'src/api/v1/quote-ApiSlice';
import { appApiSlice } from "src/api/app-ApiSlice";
import { ProjectQuoteUserProperties } from "src/model/api/project.model";
import { AcceptInviteRequest } from "src/model/api/accept-invite-request.model";

export enum ClientType {
  ELECTRICIAN = 'electrician',
}

export interface SetPropertyRequest {
  projectId: number,
  propertyKey: string,
  value: number|string
}

export interface SetProjectQuotePropertiesRequest {
  projectId: number,
  taxValue: number,
}

export const projectApiSlice = appApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjectQuoteUserProperties: builder.query<ProjectQuoteUserProperties, number | void>({
      query: (projectId: number) => `/v1/project/get-project-quote-user-properties?project_id=${projectId}`,
    }),
    getMeasurementSystem: builder.query<string, number | void>({
      query: (projectId: number) => ({
        url: `/v1/project/get-measurement-system`,
        method: 'POST',
        body: { project_id: projectId },
      }),
      transformResponse: (response: any) => response.system_name.toLowerCase(),
      providesTags: [TAG_MEASUREMENT_SYSTEM],
    }),
    setProjectQuoteProperties: builder.mutation<void, SetProjectQuotePropertiesRequest>({
      query: (payload) => ({
        url: `/v1/project/set-project-quote-properties`,
        method: 'POST',
        body: {
          project_id: payload.projectId,
          "tax-value": payload.taxValue,
        },
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
              quoteApiSlice.util.updateQueryData('getQuote', request.projectId, (draft) => {
                Object.assign(draft, { 'tax-value': request.taxValue });
              }),
          );
        } catch (error) {
          console.log('Error at setProjectQuoteProperties:', error);
        }
      },
    }),
    setProjectQuoteUserProperty: builder.mutation<void, SetPropertyRequest>({
      query: ({ projectId, propertyKey, value }) => {
        const changes = computeQuotePropertyChanges(propertyKey, value);
        const body: Record<string, number | string> = { project_id: projectId };
        changes.forEach((c) => {
          body[c.propertyKey] = c.value;
        });
        return {
          url: `/v1/project/set-project-quote-user-properties`,
          method: 'POST',
          body,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { propertyKey, value, projectId } = request;
          const changes = computeQuotePropertyChanges(propertyKey, value);
          const updates: Record<string, number | string> = {};
          changes.forEach((c) => {
            updates[c.propertyKey] = c.value;
          });
          dispatch(
              projectApiSlice.util.updateQueryData('getProjectQuoteUserProperties', projectId, (draft) => {
                Object.assign(draft, updates);
              })
          );
        } catch (err) {
          console.log('onQueryStarted hiba:', err);
        }
      },
    }),
    acceptInvite: builder.mutation<void, AcceptInviteRequest>({
      query: (arg) => ({
        url: '/v1/project/accept-invite',
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: [TAG_NOTIFICATIONS, TAG_PROJECTS],
    }),
    cloneProject: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/v1/project/clone-project?id=${id}`,
        method: 'GET',
      }),
      invalidatesTags: [TAG_PROJECTS],
    }),
    deleteProject: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/v1/project/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAG_PROJECTS],
    }),
    leaveProject: builder.mutation<void, number>({
      query: (projectId) => ({
        url: '/v1/contact/leave-occupation',
        method: 'POST',
        body: { project_id: projectId },
      }),
      invalidatesTags: [TAG_PROJECTS],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProjectQuoteUserPropertiesQuery,
  useGetMeasurementSystemQuery,
  useSetProjectQuoteUserPropertyMutation,
  useSetProjectQuotePropertiesMutation,
  useAcceptInviteMutation,
  useCloneProjectMutation,
  useDeleteProjectMutation,
  useLeaveProjectMutation,
} = projectApiSlice;